import { useState, useEffect } from "react";
import { API, TONES, BENCHMARKS, G, GL, GB, BLACK, MID, LIGHT, BORDER, BG } from "../constants";
import { SHOPIFY_BRAND_DATA } from "../data";
import { Label } from "../components/shared";
import { PageHead, Footer } from "../components/Shell";

export function S01({ tone, setTone, words, setWords, voice, setVoice, onNext, analyticsConnected, setAnalyticsConnected, setRealCVR, setRealAOV, shopConnected, shopDomain, shopInfo, shopProducts, shopLoading, onDisconnectShop, demoMode, onStartDemo, googleConnected, googleInfo, setGoogleConnected, setGoogleInfo, onSeedComplete }) {
  const [input, setInput]               = useState("");
  const [shopifyUrl, setShopifyUrl]     = useState(shopDomain || "");
  const [connState, setConnState]       = useState(shopConnected ? "connected" : "idle");
  const [connected, setConnected]       = useState(shopConnected);
  const [pulledData, setPulledData]     = useState(null);
  const [analyticsState, setAnalyticsState] = useState(analyticsConnected ? "connected" : "idle"); // idle | connecting | connected
  const [seedState, setSeedState]       = useState("idle"); // idle | seeding | done | error

  const REAL_ANALYTICS = { cvr: 0.011, aov: 138 }; // simulated real store data

  // Sync with parent shop connection state (e.g. after OAuth redirect) or demo mode
  useEffect(() => {
    if (demoMode && !connected) {
      const d = SHOPIFY_BRAND_DATA;
      setConnected(true);
      setConnState("connected");
      setShopifyUrl(d.storeUrl);
      setPulledData(d);
    } else if (shopConnected && shopInfo && !connected && !demoMode) {
      setConnected(true);
      setConnState("connected");
      setShopifyUrl(shopDomain);
      setPulledData({
        storeName: shopInfo.name || shopDomain,
        storeUrl: shopDomain,
        description: "",
        detectedTone: "technical",
        detectedVoice: "",
        detectedBannedWords: ["cheap", "affordable", "budget", "basic"],
        aboutSnippet: shopInfo.email ? `${shopInfo.name} - ${shopInfo.domain || shopDomain}` : shopInfo.name || shopDomain,
        productCount: shopProducts.length || shopInfo.product_count || 0,
        collections: [],
      });
    }
  }, [shopConnected, shopInfo, shopDomain, shopProducts, connected, demoMode]);

  const handleConnectAnalytics = async () => {
    // In demo mode, simulate Google connection with mock data
    if (demoMode) {
      setAnalyticsState("connecting");
      setTimeout(() => {
        setAnalyticsState("connected");
        setAnalyticsConnected(true);
        setRealCVR(REAL_ANALYTICS.cvr);
        setRealAOV(REAL_ANALYTICS.aov);
        if (setGoogleConnected) setGoogleConnected(true);
        if (setGoogleInfo) setGoogleInfo({
          email: "demo@siftclothing.com",
          gsc_site: "https://siftclothing.com",
          ga4_property: "properties/123456789",
        });
      }, 1800);
      return;
    }

    setAnalyticsState("connecting");
    try {
      const res = await fetch(`${API}/api/auth/google/start?shop=${encodeURIComponent(shopDomain || "")}`);
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.auth_url;
      } else {
        setAnalyticsState("idle");
      }
    } catch (e) {
      /* logged */
      setAnalyticsState("idle");
    }
  };

  const handleDisconnectAnalytics = async () => {
    setAnalyticsState("idle");
    setAnalyticsConnected(false);
    setRealCVR(BENCHMARKS.CVR);
    setRealAOV(BENCHMARKS.AOV);
    if (setGoogleConnected) setGoogleConnected(false);
    if (setGoogleInfo) setGoogleInfo(null);
    if (demoMode) return; // Skip API call in demo mode
    try {
      await fetch(`${API}/api/google/disconnect?shop=${encodeURIComponent(shopDomain || "")}`);
    } catch (e) {}
  };

  // Also update product count when shopProducts changes
  useEffect(() => {
    if (connected && pulledData && shopProducts.length > 0 && pulledData.productCount === 0) {
      setPulledData(prev => ({ ...prev, productCount: shopProducts.length }));
    }
  }, [shopProducts, connected, pulledData]);
    if (googleConnected && googleInfo && analyticsState !== "connected") {
      setAnalyticsState("connected");
      setAnalyticsConnected(true);
    }
  // Sync Google connection state from parent
  useEffect(() => {
  }, [googleConnected, googleInfo, analyticsState, setAnalyticsConnected]);

  const add = () => { const w = input.trim().toLowerCase(); if (w && !words.includes(w)) setWords([...words, w]); setInput(""); };

  const handleConnect = async () => {
    let url = shopifyUrl.trim();
    if (!url) return;
    // Strip protocol and trailing paths
    url = url.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    // Append .myshopify.com if missing
    if (!url.includes(".myshopify.com")) {
      url = url + ".myshopify.com";
    }
    setConnState("connecting");
    try {
      const res = await fetch(`${API}/api/shopify/oauth/start?shop=${encodeURIComponent(url)}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to start OAuth");
      }
      const data = await res.json();
      // Redirect to Shopify OAuth consent screen
      window.location.href = data.auth_url;
    } catch (e) {
      /* logged */
      setConnState("error");
      setTimeout(() => setConnState("idle"), 3000);
    }
  };

  const handleDisconnect = () => {
    setConnState("idle");
    setConnected(false);
    setPulledData(null);
    setShopifyUrl("");
    setTone("technical");
    setVoice("");
    setWords(["cheap", "affordable", "budget", "basic"]);
    if (onDisconnectShop) onDisconnectShop();
  };

  const handleSeedDemo = async () => {
    if (!shopDomain) return;
    setSeedState("seeding");
    try {
      const res = await fetch(`${API}/api/shopify/seed-demo?shop=${encodeURIComponent(shopDomain)}`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        setSeedState(data.success ? "done" : "error");
        if (data.success && onSeedComplete) onSeedComplete(shopDomain);
      } else {
        setSeedState("error");
      }
    } catch (e) {
      /* logged */
      setSeedState("error");
    }
  };

  const stateLabel = { idle: null, connecting: "Connecting...", connected: "Connected", error: "Connection failed" };
  const stateColor = { idle: MID, connecting: "#d97706", connected: G, error: "#dc2626" };

  return (
    <>
      <PageHead num="01" title="Brand Intelligence Setup" sub="Connect your Shopify store to pull brand voice automatically, or configure manually." onNext={onNext} />

      {/* ── Loading overlay when returning from OAuth ── */}
      {shopLoading && !connected && (
        <div style={{ border: `1px solid #fde68a`, borderRadius: 8, background: "#fffbeb", padding: "16px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 14, height: 14, border: "2px solid #fde68a", borderTopColor: "#d97706", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
          <div style={{ fontSize: 12, color: "#92400e" }}>
            <strong>Connecting your store</strong> - verifying access and fetching products...
          </div>
        </div>
      )}

      {/* ── Shopify Analytics banner ── */}
      {analyticsState !== "connected" && analyticsState !== "skipped" ? (
        <div style={{ border: `1px solid #fde68a`, borderRadius: 8, background: "#fffbeb", padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>📊</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 3 }}>Connect Google for real search data (GSC + GA4)</div>
              <div style={{ fontSize: 11, color: "#92400e", lineHeight: 1.6 }}>
                Link your Google Search Console and Analytics to power Search Intelligence with real query data, CTR, on-site search terms, and product-level performance.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
            <button data-testid="connect-google-btn" onClick={handleConnectAnalytics} disabled={analyticsState === "connecting"}
              style={{ background: analyticsState === "connecting" ? "#b45309" : "#92400e", color: "#fff", border: "none", borderRadius: 5, padding: "7px 16px", fontSize: 11, fontWeight: 700, cursor: analyticsState === "connecting" ? "not-allowed" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap", opacity: analyticsState === "connecting" ? 0.8 : 1 }}>
              {analyticsState === "connecting" ? "Redirecting to Google…" : "Connect Google"}
            </button>
            <button data-testid="skip-google-btn" onClick={() => setAnalyticsState("skipped")} style={{ background: "none", border: "none", fontSize: 11, color: "#b45309", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>Skip for now</button>
          </div>
        </div>
      ) : (
        <div style={{ border: `1px solid ${GB}`, borderRadius: 8, background: GL, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>✅</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: G, marginBottom: 2 }}>Google Search Console & Analytics connected</div>
              <div style={{ fontSize: 11, color: G }}>{googleInfo?.email ? `${googleInfo.email} - ` : ""}Real search queries and on-site data now power your Search Intelligence</div>
            </div>
          </div>
          <button onClick={handleDisconnectAnalytics} style={{ background: "none", border: `1px solid ${GB}`, borderRadius: 5, padding: "6px 12px", fontSize: 11, color: G, cursor: "pointer", fontFamily: "inherit" }}>Disconnect</button>
        </div>
      )}
      <div style={{ border: `1px solid ${connected ? GB : BORDER}`, borderRadius: 10, marginBottom: 24, overflow: "hidden", background: connected ? GL : BG, transition: "all 0.3s" }}>

        {/* Header */}
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${connected ? GB : BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: connected ? "#e8f5ee" : "#fafafa" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Shopify bag icon */}
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none">
              <path d="M15.5 5.5C15.5 3.567 13.933 2 12 2S8.5 3.567 8.5 5.5H5L3.5 20h17L19 5.5h-3.5z" stroke={connected ? G : MID} strokeWidth={1.8} strokeLinejoin="round" fill={connected ? GL : "none"} />
              <path d="M9 9V5.5C9 3.843 10.343 2.5 12 2.5S15 3.843 15 5.5V9" stroke={connected ? G : MID} strokeWidth={1.8} strokeLinecap="round" />
            </svg>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: connected ? G : BLACK }}>Shopify Store Connection</div>
              <div style={{ fontSize: 11, color: connected ? G : LIGHT }}>
                {connected && pulledData ? `${pulledData.storeName}${pulledData.productCount ? ` - ${pulledData.productCount} products` : ""}${pulledData.collections.length ? ` across ${pulledData.collections.length} collections` : ""}` : "Pull brand voice, TOV and guardrails automatically from your storefront"}
              </div>
            </div>
          </div>
          {connected && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: G, background: "#d4ead9", border: `1px solid ${GB}`, borderRadius: 4, padding: "3px 9px" }}>Connected</span>
              <button onClick={handleDisconnect} style={{ fontSize: 11, color: LIGHT, background: "none", border: `1px solid ${BORDER}`, borderRadius: 5, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}>Disconnect</button>
            </div>
          )}
        </div>

        {/* Body */}
        {!connected ? (
          <div style={{ padding: "16px 18px" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {/* URL prefix */}
              <div style={{ display: "flex", alignItems: "center", border: `1px solid ${BORDER}`, borderRadius: 6, overflow: "hidden", flex: 1 }}>
                <span style={{ padding: "8px 10px", background: "#f3f4f6", borderRight: `1px solid ${BORDER}`, fontSize: 12, color: MID, whiteSpace: "nowrap", fontFamily: "inherit" }}>https://</span>
                <input
                  data-testid="shopify-url-input"
                  value={shopifyUrl}
                  onChange={e => setShopifyUrl(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleConnect()}
                  placeholder="your-store.myshopify.com"
                  style={{ flex: 1, border: "none", padding: "8px 11px", fontSize: 13, fontFamily: "inherit", outline: "none", color: BLACK, background: BG }}
                />
              </div>
              <button
                data-testid="shopify-connect-btn"
                onClick={handleConnect}
                disabled={connState === "connecting" || !shopifyUrl.trim()}
                style={{ background: connState === "connecting" ? LIGHT : G, color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: connState === "connecting" ? "default" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "background 0.2s" }}>
                {connState === "connecting" ? "Redirecting..." : "Connect"}
              </button>
            </div>

            {/* Connecting progress */}
            {connState === "connecting" && (
              <div style={{ padding: "10px 12px", background: "#fffbeb", border: `1px solid #fde68a`, borderRadius: 6, display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 12, height: 12, border: "2px solid #fde68a", borderTopColor: "#d97706", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
                <div style={{ fontSize: 12, color: "#92400e" }}>
                  <strong>Redirecting to Shopify</strong> - you'll be asked to authorise SIFT to access your store...
                </div>
              </div>
            )}

            {/* Error state */}
            {connState === "error" && (
              <div style={{ padding: "10px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>⚠</span>
                <div style={{ fontSize: 12, color: "#dc2626" }}>
                  <strong>Connection failed</strong> - check the store URL and try again. Make sure it ends with .myshopify.com
                </div>
              </div>
            )}

            {/* What gets pulled */}
            {connState === "idle" && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["Tone of voice", "Brand voice notes", "Banned/guardrail words", "About page copy", "Collection descriptions", "Product count + structure"].map(item => (
                  <span key={item} style={{ fontSize: 11, color: MID, background: "#f3f4f6", border: `1px solid ${BORDER}`, borderRadius: 4, padding: "3px 9px" }}>{item}</span>
                ))}
              </div>
            )}

            {/* Try Demo CTA */}
            {connState === "idle" && !demoMode && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px dashed ${BORDER}`, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12, color: LIGHT }}>No store yet?</span>
                <button
                  data-testid="try-demo-btn"
                  onClick={onStartDemo}
                  style={{ background: "none", border: `1px solid #7dd3fc`, borderRadius: 5, padding: "5px 14px", fontSize: 12, fontWeight: 600, color: "#0369a1", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}
                  onMouseEnter={e => { e.target.style.background = "#f0f9ff"; }}
                  onMouseLeave={e => { e.target.style.background = "none"; }}
                >
                  Try demo with sample data
                </button>
                <span style={{ fontSize: 11, color: LIGHT }}>3 SKUs, 11 signal sources, full 7-step walkthrough</span>
              </div>
            )}
          </div>
        ) : pulledData ? (
          /* Connected - show what was pulled */
          <div style={{ padding: "16px 18px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 10 }}>Pulled from store</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              {[
                { label: "Store name",      val: pulledData.storeName || shopDomain },
                { label: "Tone detected",   val: TONES.find(t => t.id === pulledData.detectedTone)?.label || pulledData.detectedTone },
                { label: "Products",        val: `${pulledData.productCount || 0} products` },
                { label: "Guardrail words", val: pulledData.detectedBannedWords?.slice(0,4).join(", ") + (pulledData.detectedBannedWords?.length > 4 ? ` +${pulledData.detectedBannedWords.length - 4}` : "") },
              ].map(row => (
                <div key={row.label} style={{ background: BG, border: `1px solid ${GB}`, borderRadius: 6, padding: "9px 12px" }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: G, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>{row.label}</div>
                  <div style={{ fontSize: 12, color: BLACK, lineHeight: 1.5 }}>{row.val}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: G, fontStyle: "italic" }}>Fields below have been pre-populated from your store. You can edit them before continuing.</div>
            {/* Seed demo catalogue */}
            {!demoMode && (
              <div data-testid="seed-demo-section" style={{ marginTop: 14, paddingTop: 14, borderTop: `1px dashed ${GB}`, display: "flex", alignItems: "center", gap: 10 }}>
                <button
                  data-testid="seed-demo-btn"
                  onClick={handleSeedDemo}
                  disabled={seedState === "seeding"}
                  style={{
                    background: seedState === "done" ? GL : seedState === "error" ? "#fef2f2" : "none",
                    border: `1px solid ${seedState === "done" ? GB : seedState === "error" ? "#fecaca" : "#7dd3fc"}`,
                    borderRadius: 5, padding: "5px 14px", fontSize: 12, fontWeight: 600,
                    color: seedState === "done" ? G : seedState === "error" ? "#dc2626" : "#0369a1",
                    cursor: seedState === "seeding" ? "not-allowed" : "pointer", fontFamily: "inherit",
                    opacity: seedState === "seeding" ? 0.7 : 1,
                  }}
                >
                  {seedState === "seeding" ? "Seeding..." : seedState === "done" ? "Catalogue seeded" : seedState === "error" ? "Seed failed - retry" : "Seed SIFT demo catalogue"}
                </button>
                <span style={{ fontSize: 11, color: seedState === "done" ? G : LIGHT }}>
                  {seedState === "done"
                    ? "3 streetwear SKUs created. Refresh products to see them."
                    : "Replace store products with 3 SIFT Streetwear demo SKUs"
                  }
                </span>
              </div>
            )}
          </div>
        ) : connected ? (
          /* Connected but still loading data */
          <div style={{ padding: "16px 18px", textAlign: "center" }}>
            <div style={{ fontSize: 12, color: MID }}>Loading store data...</div>
          </div>
        ) : null}
      </div>

      {/* ── Manual config ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>

        {/* Tone list */}
        <div>
          <Label>TONE OF VOICE {connected && <span style={{ fontSize: 10, fontWeight: 700, color: G, marginLeft: 6, background: GL, border: `1px solid ${GB}`, borderRadius: 3, padding: "1px 6px" }}>auto-detected</span>}</Label>
          <div style={{ border: `1px solid ${connected ? GB : BORDER}`, borderRadius: 8, overflow: "hidden" }}>
            {TONES.map((t, i) => {
              const on = tone === t.id;
              return (
                <div key={t.id} onClick={() => setTone(t.id)}
                  style={{ padding: "12px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", background: on ? GL : BG, borderBottom: i < TONES.length - 1 ? `1px solid ${BORDER}` : "none", borderLeft: `3px solid ${on ? G : "transparent"}`, transition: "background 0.1s" }}>
                  <div>
                    <div style={{ fontWeight: on ? 700 : 500, fontSize: 13, color: on ? G : BLACK }}>{t.label}</div>
                    <div style={{ fontSize: 11, color: LIGHT, marginTop: 2 }}>{t.sub}</div>
                  </div>
                  {on && <span style={{ width: 7, height: 7, borderRadius: "50%", background: G, flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <div>
            <Label>BANNED WORDS {connected && <span style={{ fontSize: 10, fontWeight: 700, color: G, marginLeft: 6, background: GL, border: `1px solid ${GB}`, borderRadius: 3, padding: "1px 6px" }}>auto-detected</span>}</Label>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && add()}
                placeholder="add a word..."
                style={{ flex: 1, border: `1px solid ${connected ? GB : BORDER}`, borderRadius: 6, padding: "8px 11px", fontSize: 13, fontFamily: "inherit", outline: "none", color: BLACK }} />
              <button onClick={add} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Add</button>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {words.map(w => (
                <span key={w} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: connected ? GL : "#f5f5f5", border: `1px solid ${connected ? GB : BORDER}`, borderRadius: 4, fontSize: 12, padding: "3px 8px", color: connected ? G : MID }}>
                  {w} <span onClick={() => setWords(words.filter(x => x !== w))} style={{ color: LIGHT, cursor: "pointer", fontSize: 13, lineHeight: 1, marginTop: 1 }}>×</span>
                </span>
              ))}
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <Label>BRAND VOICE NOTES {connected && <span style={{ fontSize: 10, fontWeight: 700, color: G, marginLeft: 6, background: GL, border: `1px solid ${GB}`, borderRadius: 3, padding: "1px 6px" }}>auto-detected</span>}</Label>
            <textarea value={voice} onChange={e => setVoice(e.target.value)}
              placeholder="e.g. Never use exclamation marks. Lead with silhouette before material."
              style={{ width: "100%", minHeight: 128, border: `1px solid ${connected ? GB : BORDER}`, borderRadius: 6, padding: "10px 12px", fontSize: 13, fontFamily: "inherit", color: BLACK, resize: "vertical", outline: "none", lineHeight: 1.6, background: connected ? "#fafffe" : BG, transition: "all 0.3s" }} />
          </div>
        </div>
      </div>

      <Footer onNext={onNext} />
    </>
  );

}
